import React, { useEffect } from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useAcademyPolicy,
  useUpdateAcademyPolicy,
} from "../hooks/useAcademyPolicy";

const DEFAULTS = {
  min_minutes_to_book: 30,
  min_minutes_to_cancel: 120,
  max_days_ahead_to_book: 14,
  allow_topic_repeat: true,
  default_session_minutes: 60,
  allow_partial_payments: false,
  deliver_hours_on_full_payment: false,
  max_days_to_settle: 30,
};

function AcademyPolicyForm() {
  const { data: policy, isLoading, isError } = useAcademyPolicy();
  const updateMutation = useUpdateAcademyPolicy();
  const [values, setValues] = React.useState(DEFAULTS);
  const [dirty, setDirty] = React.useState(false);

  // Populate form when policy loads (GET auto-creates singleton on first call)
  useEffect(() => {
    if (policy) {
      setValues({
        min_minutes_to_book: policy.min_minutes_to_book ?? DEFAULTS.min_minutes_to_book,
        min_minutes_to_cancel: policy.min_minutes_to_cancel ?? DEFAULTS.min_minutes_to_cancel,
        max_days_ahead_to_book: policy.max_days_ahead_to_book ?? DEFAULTS.max_days_ahead_to_book,
        allow_topic_repeat: policy.allow_topic_repeat ?? DEFAULTS.allow_topic_repeat,
        default_session_minutes: policy.default_session_minutes ?? DEFAULTS.default_session_minutes,
        allow_partial_payments: policy.allow_partial_payments ?? DEFAULTS.allow_partial_payments,
        deliver_hours_on_full_payment: policy.deliver_hours_on_full_payment ?? DEFAULTS.deliver_hours_on_full_payment,
        max_days_to_settle: policy.max_days_to_settle ?? DEFAULTS.max_days_to_settle,
      });
      setDirty(false);
    }
  }, [policy]);

  const set = (field) => (e) => {
    const val =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [field]: val }));
    setDirty(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = {
      ...values,
      min_minutes_to_book: Number(values.min_minutes_to_book),
      min_minutes_to_cancel: Number(values.min_minutes_to_cancel),
      max_days_ahead_to_book: Number(values.max_days_ahead_to_book),
      default_session_minutes: Number(values.default_session_minutes),
      max_days_to_settle: Number(values.max_days_to_settle),
    };
    updateMutation.mutate(body, {
      onSuccess: () => {
        toast.success("Políticas actualizadas");
        setDirty(false);
      },
      onError: () => toast.error("Error al guardar las políticas"),
    });
  };

  if (isLoading)
    return <Spinner animation="border" role="status" aria-label="Cargando políticas" />;
  if (isError)
    return <Alert variant="danger">Error al cargar las políticas de la academia.</Alert>;

  return (
    <Card>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="policy-min-book">
            <Form.Label>
              Minutos mínimos para agendar antes de la clase
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={values.min_minutes_to_book}
              onChange={set("min_minutes_to_book")}
              style={{ minHeight: 44 }}
            />
            <Form.Text muted>
              El estudiante debe agendar al menos N minutos antes del inicio.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="policy-min-cancel">
            <Form.Label>
              Minutos mínimos para cancelar antes de la clase
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={values.min_minutes_to_cancel}
              onChange={set("min_minutes_to_cancel")}
              style={{ minHeight: 44 }}
            />
            <Form.Text muted>
              El estudiante puede cancelar hasta N minutos antes del inicio.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="policy-max-days">
            <Form.Label>
              Días máximos de anticipación para agendar
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={values.max_days_ahead_to_book}
              onChange={set("max_days_ahead_to_book")}
              style={{ minHeight: 44 }}
            />
            <Form.Text muted>
              El calendario muestra clases disponibles hasta N días adelante.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="policy-session-minutes">
            <Form.Label>Duración estándar de la sesión (minutos)</Form.Label>
            <Form.Control
              type="number"
              min="15"
              step="15"
              value={values.default_session_minutes}
              onChange={set("default_session_minutes")}
              style={{ minHeight: 44 }}
            />
            <Form.Text muted>
              Se precarga en el grade card al registrar una clase.
            </Form.Text>
          </Form.Group>

          <Form.Check
            type="switch"
            id="policy-allow-repeat"
            label="Permitir repetir temas ya cursados"
            checked={Boolean(values.allow_topic_repeat)}
            onChange={set("allow_topic_repeat")}
            className="mb-4"
            style={{ minHeight: 44 }}
          />

          <hr />
          <h6 className="text-uppercase text-muted small mb-3">
            Cartera / pagos
          </h6>

          <Form.Check
            type="switch"
            id="policy-allow-partial"
            label="Permitir pagos parciales (queda saldo pendiente en cartera)"
            checked={Boolean(values.allow_partial_payments)}
            onChange={set("allow_partial_payments")}
            className="mb-3"
            style={{ minHeight: 44 }}
          />

          <Form.Check
            type="switch"
            id="policy-deliver-full"
            label="Entregar las horas solo cuando el estudiante salde el total"
            checked={Boolean(values.deliver_hours_on_full_payment)}
            onChange={set("deliver_hours_on_full_payment")}
            className="mb-3"
            style={{ minHeight: 44 }}
            disabled={!values.allow_partial_payments}
          />

          <Form.Group className="mb-4" controlId="policy-settle-days">
            <Form.Label>Días máximos para saldar el excedente</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={values.max_days_to_settle}
              onChange={set("max_days_to_settle")}
              style={{ minHeight: 44 }}
              disabled={!values.allow_partial_payments}
            />
            <Form.Text muted>
              Pasados estos días sin saldar, el paquete se congela y el
              estudiante no podrá agendar hasta pagar lo pendiente.
            </Form.Text>
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            disabled={updateMutation.isPending || !dirty}
            style={{ minHeight: 44, minWidth: 160 }}
          >
            {updateMutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Guardar políticas"
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

export { AcademyPolicyForm };
